import React, { useState, useEffect } from 'react';
import { organizationSettingsService } from 'core/API/organizationSettings';
import { userSettingsService } from 'core/API/userSettings';
import { useOrganizationCrud } from 'core/hooks/useOrganizationCrud';
import styles from './EditOrganisationSettings.module.scss';
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import { Button } from '../../../../../../components/ui/button';
import { Button as MuiButton } from '@mui/material';
import { Autocomplete, Grid, FormControl, Box, Typography, IconButton, Chip, CircularProgress } from '@mui/material';
import * as Yup from 'yup';
import { COUNTRY_LIST, COUNTRY_STATES } from '../../constants';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FiscalCalendarForm from '../../../../../../core/components/FiscalCalendarForm';
import { Organization, ProductLicensed } from 'types';

export interface WorkingHours {
	day: {
		id: number;
		label: string;
		locale: string;
		position: string;
	};
	day_end: {
		id: number;
		label: string;
		locale: string;
		position: string;
	};
	open_at: string;
	close_at: string;
	closed: boolean;
}

export interface DayPart {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
}

interface EditOrganizationSettingProps {
	organization: Organization;
	handleBackClick?: () => void;
	onOrganizationUpdated?: () => void;
}

const EditOrganizationSettings: React.FC<EditOrganizationSettingProps> = ({
	organization,
	handleBackClick,
	onOrganizationUpdated,
}) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [apiSuccess, setApiSuccess] = useState<string | null>(null);
	const [organizationDayParts, setOrganizationDayParts] = useState<DayPart[]>([]);
	const [deletedDayPartIds, setDeletedDayPartIds] = useState<string[]>([]);
	const [adminUsers, setAdminUsers] = useState<{ id?: number; name: string; email?: string }[]>([]);

	const {
		timeZones,
		days,
		dayParts,
		fetchDays,
		fetchDayParts,
		createDayPart,
		updateDayPart,
		deleteDayPart,
		fetchTimeZones,
	} = useOrganizationCrud();

	React.useEffect(() => {
		fetchTimeZones();
	}, [fetchTimeZones]);

	// Load users who have organization-admin style security roles for the admin dropdown
	useEffect(() => {
		const loadAdminUsers = async () => {
			try {
				const res = await userSettingsService.getUsers({ limit: 1000, active: true });
				const users = res?.data || [];

				const looksLikeOrgAdmin = (role: any) => {
					if (!role) return false;
					const roleName = (role.name || '').toString().toLowerCase();
					// Match common variants: organization/organisation + admin
					return /organ.*admin/.test(roleName) || /organisation.*admin/.test(roleName);
				};

				const filtered = users
					.filter((u: any) => looksLikeOrgAdmin(u.securityRole))
					.map((u: any) => ({ id: u.id, name: u.name, email: u.email }));

				setAdminUsers(filtered);
			} catch (error) {
				console.error('Failed to load admin users:', error);
			}
		};

		loadAdminUsers();
	}, []);

	// Load dropdown data on mount
	useEffect(() => {
		fetchDays();
	}, [fetchDays]);

	// Fetch day parts for the organization
	useEffect(() => {
		if (organization?.id) {
			fetchDayParts(organization.id);
		}
	}, [organization?.id, fetchDayParts]);

	// Transform API day parts to local DayPart format and update form when dayParts change
	useEffect(() => {
		if (dayParts.length > 0) {
			const transformedDayParts: DayPart[] = dayParts.map((apiDayPart: any) => ({
				id: apiDayPart.id.toString(),
				name: apiDayPart.partName,
				startTime: apiDayPart.startTime,
				endTime: apiDayPart.endTime,
			}));
			setOrganizationDayParts(transformedDayParts);
		}
	}, [dayParts]);

	// Update formik values when organization day parts are loaded
	useEffect(() => {
		if (organizationDayParts.length > 0 && formik.values.day_parts) {
			// Only update if we currently have the default day part
			if (formik.values.day_parts.length === 1 && formik.values.day_parts[0].id === 'daypart-1') {
				formik.setFieldValue('day_parts', organizationDayParts);
			}
		}
	}, [organizationDayParts]); // eslint-disable-line react-hooks/exhaustive-deps

	const validationSchema = Yup.object().shape({
		name: Yup.string().required('Organization name is required'),
		// addressLine1: Yup.string().required('Street address is required'),
		city: Yup.string().required('City is required'),
		postcode: Yup.string().required('Postal code is required'),
		state: Yup.string().required('State is required'),
		country: Yup.string().required('Country is required'),
		start_of_day: Yup.string().required('Start of day is required'),
		start_of_week: Yup.string().required('Start of week is required'),
		fiscalStartOfYear: Yup.string().required('Fiscal start of year is required'),
		fiscalCalendarType: Yup.string().required('Fiscal calendar type is required'),
		fiscalPeriodType: Yup.number().when('fiscalCalendarType', {
			is: 'fiscal',
			then: (schema) => schema.required('Fiscal period type is required'),
			otherwise: (schema) => schema,
		}),
		timeZone: Yup.string().required('Time Zone is required'),
		day_parts: Yup.array()
			.min(1, 'At least one day part is required')
			.test('valid-day-parts', 'Invalid day parts configuration', function (dayParts) {
				if (!dayParts || dayParts.length === 0) return false;

				// Convert time string to minutes since midnight
				const timeToMinutes = (time: string) => {
					const [hours, minutes] = time.split(':').map(Number);
					return hours * 60 + minutes;
				};

				// Validate time format
				const isValidTimeFormat = (time: string) => {
					return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
				};

				const firstPart = dayParts[0] as DayPart;
				const lastPart = dayParts[dayParts.length - 1] as DayPart;
				const startOfDay = this.parent?.start_of_day;

				// Skip validation if start_of_day is not present or invalid
				if (!startOfDay || !isValidTimeFormat(startOfDay)) {
					return true;
				}

				// Ensure first part starts at start of day
				if (firstPart.startTime !== startOfDay) {
					return this.createError({
						message: `First day part must start at ${startOfDay}`,
					});
				}

				// Calculate total duration
				const startMinutes = timeToMinutes(firstPart.startTime);
				let endMinutes = timeToMinutes(lastPart.endTime);

				// If end time is less than start time, it means it's the next day
				if (endMinutes <= startMinutes) {
					endMinutes += 24 * 60; // Add 24 hours worth of minutes
				}

				const totalDuration = endMinutes - startMinutes;
				if (totalDuration > 24 * 60) {
					return this.createError({
						message: 'Total duration of day parts cannot exceed 24 hours',
					});
				}

				// Validate contiguous time slots
				// for (let i = 0; i < dayParts.length - 1; i++) {
				// 	const currentPart = dayParts[i];
				// 	const nextPart = dayParts[i + 1];

				// 	if (currentPart.endTime !== nextPart.startTime) {
				// 		return this.createError({
				// 			message: 'Day parts must be contiguous with no gaps',
				// 		});
				// 	}
				// }

				return true;
			}),
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			name: organization?.name || '',
			addressLine1: organization?.addressLine1 || '',
			organization_administrator: organization?.organization_administrator || { name: '' },
			city: organization?.city || '',
			state: organization?.state || '',
			postcode: organization?.postcode || '',
			country: organization?.country || 'USA',
			timeZone: organization?.timeZone || '',
			products: organization?.products || ([] as ProductLicensed[]),
			// Business Day Configuration
			start_of_day: organization?.start_of_day || '05:00',
			start_of_week: organization?.start_of_week || '/api/days/1', // Default to first day (Monday typically)
			day_parts:
				organizationDayParts.length > 0
					? organizationDayParts
					: organization?.day_parts ||
						([
							{
								id: 'daypart-1',
								name: 'Business Hours',
								startTime: organization?.start_of_day || '05:00',
								endTime: organization?.start_of_day || '05:00',
							},
						] as DayPart[]),
			// Fiscal Calendar Configuration
			fiscalStartOfYear: organization?.fiscalStartOfYear || '01',
			fiscalCalendarType: organization?.fiscalCalendarType || 'calendar',
			fiscalPeriodType: organization?.fiscalPeriodType || 12,
			fiscalPeriods: organization?.fiscalPeriods || Array(13).fill(4),
		},
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				setApiError(null);
				setApiSuccess(null);

				// First, handle any deleted day parts
				for (const dayPartId of deletedDayPartIds) {
					if (!dayPartId.startsWith('temp-')) {
						await deleteDayPart(parseInt(dayPartId));
					}
				}

				// Process day parts changes
				for (const dayPart of values.day_parts) {
					if (dayPart.id.startsWith('temp-')) {
						// Create new day part
						await createDayPart(organization.id, {
							partName: dayPart.name,
							startTime: dayPart.startTime,
							endTime: dayPart.endTime,
						});
					} else {
						// Update existing day part
						await updateDayPart(parseInt(dayPart.id), {
							organization: `/api/organization/${organization.id}`,
							partName: dayPart.name,
							startTime: dayPart.startTime,
							endTime: dayPart.endTime,
						});
					}
				}

				// Call the API to update the organization
				const payload = {
					...values,
					id: organization.id,
				};
				await organizationSettingsService.updateOrganization(payload);

				setApiSuccess('Organization updated successfully!');

				// Clear deleted day parts list
				setDeletedDayPartIds([]);

				// Call the callback to refresh the organization list
				onOrganizationUpdated?.();

				// Navigate back after a short delay
				setTimeout(() => {
					handleBackClick?.();
				}, 1500);
			} catch (error: any) {
				setApiError(error.message || 'Failed to update organization');
			} finally {
				setSubmitting(false);
			}
		},
	});

	// Set default start_of_week to day with position "0" when days are loaded
	useEffect(() => {
		if (days.length > 0 && !organization?.start_of_week && formik) {
			const defaultDay = days.find((day) => day.position === '0');
			if (defaultDay) {
				formik.setFieldValue('start_of_week', defaultDay['@id']);
			}
		}
	}, [days, organization?.start_of_week]); // eslint-disable-line react-hooks/exhaustive-deps

	// Update day parts when start_of_day changes
	useEffect(() => {
		if (
			formik.values.day_parts &&
			formik.values.day_parts.length > 0 &&
			formik.values.start_of_day
		) {
			const currentDayParts = formik.values.day_parts;
			const updatedDayParts = currentDayParts.map((dp, index) => {
				if (index === 0) {
					// First day part should start at start_of_day
					return { ...dp, startTime: formik.values.start_of_day };
				}
				if (index === currentDayParts.length - 1) {
					// Last day part should end at start_of_day
					return { ...dp, endTime: formik.values.start_of_day };
				}
				return dp;
			});

			formik.setFieldValue('day_parts', updatedDayParts);
		}
	}, [formik.values.start_of_day]); // eslint-disable-line react-hooks/exhaustive-deps
		// Determine tenant id from localStorage if available, otherwise fall back to organization.id
	const tenantId = React.useMemo(() => {
		const orgId = localStorage.getItem('BYOD-organizationId');
		// fallback to organization id if nothing found in localStorage
		return orgId || organization?.id;
	}, [organization?.id]);

	if (!organization) {
		return <div className={styles.loading}><CircularProgress size={20} color="inherit" /></div>;
	}

	const getStateOptions = () => {
		if (!formik.values.country) return [];
		return COUNTRY_STATES[formik.values.country] || [];
	};

	// Day Parts Management Functions
	const addDayPart = () => {
		const currentDayParts = formik.values.day_parts || [];
		if (currentDayParts.length >= 10) {
			setApiError('Maximum 10 day parts allowed');
			setTimeout(() => setApiError(null), 5000);
			return;
		}

		// Create a temporary day part with a unique temp ID
		const tempId = `temp-${Date.now()}`;
		const lastDayPart = currentDayParts[currentDayParts.length - 1];

		// Create new day part with default values
		const newDayPart: DayPart = {
			id: tempId,
			name: `Day Part ${currentDayParts.length + 1}`,
			startTime: lastDayPart ? lastDayPart.endTime : formik.values.start_of_day,
			endTime: lastDayPart ? formik.values.start_of_day : formik.values.start_of_day,
		};

		// Update the form with the new temporary day part
		formik.setFieldValue('day_parts', [...currentDayParts, newDayPart]);
	};

	const removeDayPart = (id: string) => {
		const currentDayParts = formik.values.day_parts || [];
		if (currentDayParts.length <= 1) return; // Must have at least one day part

		const updatedDayParts = currentDayParts.filter((dp) => dp.id !== id);
		// Recalculate start times to ensure continuity
		const adjustedDayParts = updatedDayParts.map((dp, index) => {
			if (index === 0) return dp;
			return {
				...dp,
				startTime: updatedDayParts[index - 1].endTime,
			};
		});

		// Add to deleted day parts if it's not a temporary id
		if (!id.startsWith('temp-')) {
			setDeletedDayPartIds((prev) => [...prev, id]);
		}

		formik.setFieldValue('day_parts', adjustedDayParts);
	};

	const updateDayPartName = (id: string, name: string) => {
		const currentDayParts = formik.values.day_parts || [];

		// Update local state immediately for responsive UI
		const updatedDayParts = currentDayParts.map((dp) => (dp.id === id ? { ...dp, name } : dp));
		formik.setFieldValue('day_parts', updatedDayParts);
	};

	const updateDayPartStartTime = (id: string, startTime: string) => {
		const currentDayParts = formik.values.day_parts || [];

		// Update local state immediately for responsive UI
		const updatedDayParts = currentDayParts.map((dp) => (dp.id === id ? { ...dp, startTime } : dp));
		formik.setFieldValue('day_parts', updatedDayParts);
	};

	const updateDayPartEndTime = (id: string, endTime: string) => {
		const currentDayParts = formik.values.day_parts || [];
		const dayPartIndex = currentDayParts.findIndex((dp) => dp.id === id);

		if (dayPartIndex === -1) return;

		// Update local state immediately for responsive UI
		const updatedDayParts = currentDayParts.map((dp, index) => {
			if (dp.id === id) {
				return { ...dp, endTime };
			}
			// Update start time of next day part
			if (index === dayPartIndex + 1) {
				return { ...dp, startTime: endTime };
			}
			return dp;
		});

		formik.setFieldValue('day_parts', updatedDayParts);
	};

	return (
		<Grid container className={styles.editOrganizationSetting} direction={'column'}>
			<Grid item spacing={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</Grid>
					<Grid>
						<Button
							className={styles.backButton}
							onClick={() => formik.handleSubmit()}
							disabled={formik.isSubmitting}
						>
							{formik.isSubmitting ? 'Saving...' : 'Save'}
						</Button>
					</Grid>
				</Grid>
				<Grid container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Organization Details</h2>
				</Grid>
			</Grid>

			{/* API Feedback */}
			{apiSuccess && <div className={styles.successMessage}>{apiSuccess}</div>}
			{apiError && <div className={styles.errorMessage}>{apiError}</div>}

			<h3 className={styles.title}>
				{organization.name} ({tenantId})
			</h3>
			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* Name */}
				<TextField
					label="Name"
					name="name"
					value={formik.values.name}
					// Organization name is not editable in this form
					disabled
					error={formik.touched.name && Boolean(formik.errors.name)}
					helperText={formik.touched.name && (formik.errors.name as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				{/* Address */}
				<TextField
					label="Street Address"
					name="addressLine1"
					required={true}
					value={formik.values.addressLine1}
					onChange={formik.handleChange}
					error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
					helperText={formik.touched.addressLine1 && (formik.errors.addressLine1 as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>
				<TextField
					label="City"
					name="city"
					required={true}
					value={formik.values.city}
					onChange={formik.handleChange}
					error={formik.touched.city && Boolean(formik.errors.city)}
					helperText={formik.touched.city && (formik.errors.city as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				{/* State */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={getStateOptions()}
						getOptionLabel={(option) => option}
						value={formik.values.state || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('state', newValue || '');
						}}
						disabled={!formik.values.country}
						renderInput={(params) => (
							<TextField
								{...params}
								label="State"
								error={formik.touched.state && Boolean(formik.errors.state)}
								helperText={formik.touched.state && (formik.errors.state as string)}
								required
							/>
						)}
					/>
				</FormControl>

				<TextField
					label="Postal Code"
					name="postcode"
					required={true}
					value={formik.values.postcode}
					onChange={formik.handleChange}
					error={formik.touched.postcode && Boolean(formik.errors.postcode)}
					helperText={formik.touched.postcode && (formik.errors.postcode as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				{/* Country */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={COUNTRY_LIST}
						getOptionLabel={(option) => option}
						value={formik.values.country || null}
						onChange={(_, newValue) => {
							const newCountry = newValue || '';
							formik.setFieldValue('country', newCountry);
							formik.setFieldValue('state', ''); // Reset state when country changes
							// If a timezone is selected but its country doesn't match the new country, clear it
							const selectedTz = timeZones.find((tz: any) => tz['@id'] === formik.values.timeZone);
							if (selectedTz && (selectedTz.country || (selectedTz as any).country_name)) {
								const tzCountry = (selectedTz.country || (selectedTz as any).country_name || '').toString();
								if (tzCountry.toLowerCase() !== newCountry.toString().toLowerCase()) {
									formik.setFieldValue('timeZone', '');
								}
							}
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Country"
								error={formik.touched.country && Boolean(formik.errors.country)}
								helperText={formik.touched.country && (formik.errors.country as string)}
								required
							/>
						)}
					/>
				</FormControl>

				{/* Time Zone */}
				<Autocomplete
					options={
						formik.values.country
							? timeZones.filter((tz: any) => {
								const country = (tz?.country || (tz as any)?.country_name || '').toString();
								return (
									!country ||
									country.toLowerCase() === formik.values.country.toString().toLowerCase()
								);
							})
							: timeZones
					}
					getOptionLabel={(option) => option.label}
					value={timeZones.find((tz) => tz['@id'] === formik.values.timeZone) || null}
					onChange={(_, value) => {
						const timeZoneValue = value?.['@id'] || '';
						formik.setFieldValue('timeZone', timeZoneValue);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Time Zone"
							required
							error={formik.touched.timeZone && Boolean(formik.errors.timeZone)}
							helperText={formik.touched.timeZone && (formik.errors.timeZone as string)}
							className={styles.input}
							margin="normal"
							fullWidth
						/>
					)}
				/>

				{/* Organization Admin Autocomplete - list of users who appear to have org admin roles */}
				<Autocomplete
					options={adminUsers}
					getOptionLabel={(option) => option?.name || ''}
					value={
						adminUsers.find((u) => u.name === formik.values.organization_administrator?.name) ||
						(formik.values.organization_administrator?.name
							? { id: undefined, name: formik.values.organization_administrator?.name }
							: null)
					}
					onChange={(_, value) => {
						formik.setFieldValue('organization_administrator.name', value?.name || '');
						formik.setFieldValue('organization_administrator.email', value?.email || '');
					}}
					renderOption={(props, option) => (
						<li {...props} key={option.id || option.name}>
							{option.name} {option.email ? `(${option.email})` : ''}
						</li>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							required={true}
							label="Organization Administrator"
							className={styles.input}
							margin="normal"
							fullWidth
						/>
					)}
				/>

				{/* Products Licensed Section */}
				<Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', my: 2 }}>
					<Typography
						fontFamily={'Gotham'}
						color={'rgb(0 0 0 / 60%)'}
						variant="subtitle2"
						sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 2 }}
					>
						Products Licensed
					</Typography>

					{formik.values.products && formik.values.products.length > 0 ? (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
							{formik.values.products.map((product) => (
								<Chip key={product.id} label={product.label} variant="outlined" color="primary" />
							))}
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
							No products licensed
						</Typography>
					)}
				</Box>

				<Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', my: 2 }}>
					{/* Business Day Configuration Section */}
					<Grid item xs={12} sx={{ my: 2 }}>
						<Typography
							fontFamily={'Gotham'}
							color={'rgb(0 0 0 / 60%)'}
							variant="subtitle2"
							sx={{ fontSize: '0.875rem', fontWeight: 500 }}
						>
							Business Day Configuration
						</Typography>
					</Grid>

					{/* Start of Day */}
					<Grid container spacing={2} sx={{ mb: 2 }}>
						<Grid item xs={6}>
							<TextField
								name="start_of_day"
								label="Start of Day"
								type="time"
								required
								value={formik.values.start_of_day || '05:00'}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								className={styles.input}
								margin="normal"
								fullWidth
							/>
						</Grid>

						{/* Start of Week */}
						<Grid item xs={6}>
							<FormControl fullWidth margin="normal">
								<Autocomplete
									options={days}
									getOptionLabel={(option) => option.label}
									value={days?.find((day) => day['@id'] === formik.values.start_of_week) || null}
									onChange={(_, newValue) => {
										formik.setFieldValue('start_of_week', newValue ? newValue['@id'] : '');
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Start of Week"
											required
											error={formik.touched.start_of_week && Boolean(formik.errors.start_of_week)}
											helperText={
												formik.touched.start_of_week && (formik.errors.start_of_week as string)
											}
										/>
									)}
								/>
							</FormControl>
						</Grid>
					</Grid>

					{/* Day Parts Section */}
					<Box sx={{ mb: 3 }}>
						<Box
							sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
						>
							<Typography
								variant="body1"
								fontFamily={'Gotham'}
								color={'rgb(0 0 0 / 60%)'}
								sx={{ fontSize: '0.875rem', fontWeight: 500 }}
							>
								Day Parts
							</Typography>
							<MuiButton
								onClick={addDayPart}
								disabled={(formik.values.day_parts || []).length >= 10}
								startIcon={<AddIcon />}
								className={styles.addButton}
								style={{ textTransform: 'none' }}
							>
								Add Day Part
							</MuiButton>
						</Box>

						{(formik.values.day_parts || []).map((dayPart, _index) => (
							<Grid container spacing={2} key={dayPart.id} sx={{ mb: 2, alignItems: 'center' }}>
								<Grid item xs={3}>
									<TextField
										label="Day Part Name"
										value={dayPart.name}
										onChange={(e) => updateDayPartName(dayPart.id, e.target.value)}
										fullWidth
										size="small"
									/>
								</Grid>
								<Grid item xs={3}>
									<TextField
										label="Start Time"
										type="time"
										value={dayPart.startTime}
										onChange={(e) => updateDayPartStartTime(dayPart.id, e.target.value)}
										fullWidth
										size="small"
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid item xs={3}>
									<TextField
										label="End Time"
										type="time"
										value={dayPart.endTime}
										onChange={(e) => updateDayPartEndTime(dayPart.id, e.target.value)}
										fullWidth
										size="small"
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid item xs={3} sx={{ display: 'flex', gap: 1 }}>
									<IconButton
										onClick={() => removeDayPart(dayPart.id)}
										disabled={(formik.values.day_parts || []).length <= 1}
										color="error"
										size="small"
										title="Delete day part"
									>
										<DeleteIcon />
									</IconButton>
								</Grid>
							</Grid>
						))}
					</Box>
				</Box>

				<Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', my: 2 }}>
					{/* Fiscal Calendar Configuration Section */}
					<Grid item xs={12} sx={{ mt: 3, mb: 2 }}>
						<Typography
							fontFamily={'Gotham'}
							color={'rgb(0 0 0 / 60%)'}
							variant="subtitle2"
							sx={{ fontSize: '0.875rem', fontWeight: 500 }}
						>
							Fiscal Calendar Configuration
						</Typography>
					</Grid>

					<FiscalCalendarForm
						startOfDay={formik.values.start_of_day}
						startOfWeek={formik.values.start_of_week}
						values={{
							startOfYear: formik.values.fiscalStartOfYear,
							calendarType: formik.values.fiscalCalendarType,
							periodType: formik.values.fiscalPeriodType,
							periods: formik.values.fiscalPeriods,
						}}
						onChange={(field, value) => {
							formik.setFieldValue(field, value);
						}}
						errors={{
							startOfYear: formik.touched.fiscalStartOfYear && formik.errors.fiscalStartOfYear,
							calendarType: formik.touched.fiscalCalendarType && formik.errors.fiscalCalendarType,
							periodType: formik.touched.fiscalPeriodType && formik.errors.fiscalPeriodType,
						}}
					/>
				</Box>
			</form>
		</Grid>
	);
};

export default EditOrganizationSettings;
